/**
 * OTPVerify.jsx
 * Email verification page — tells user to check their email for a verification link.
 * After clicking the link, they are redirected to /pending-approval.
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function OTPVerify() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const email       = location.state?.email || "";
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [resent,    setResent]    = useState(false);
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    if (!email) { navigate("/signup"); return; }
    const timer = setInterval(() => setCountdown(c => c > 0 ? c - 1 : 0), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleResend = async () => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      await supabase.auth.resend({ type: "signup", email });
      setResent(true);
      setCountdown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#0a0b0f",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "sans-serif", padding: 24,
    }}>
      <div style={{
        background: "#12141a", border: "1px solid #2a2d3a",
        borderRadius: 16, padding: "40px 36px",
        width: "100%", maxWidth: 420, textAlign: "center",
      }}>

        {/* Icon */}
        <div style={{
          width: 64, height: 64, borderRadius: 16,
          background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 24px", fontSize: 28,
        }}>✉️</div>

        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: "0 0 8px" }}>
          Check your email
        </h1>

        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 6px", lineHeight: 1.6 }}>
          We have sent a verification email to
        </p>

        <p style={{ fontSize: 14, fontWeight: 700, color: "#818cf8", margin: "0 0 16px" }}>
          {email}
        </p>

        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 28px", lineHeight: 1.7 }}>
          Please click the verification link in your email to confirm your account.
          Once verified, your account will be reviewed by our team before you can log in.
        </p>

        {/* Info box */}
        <div style={{
          background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: 10, padding: "12px 16px",
          fontSize: 12, color: "#818cf8", lineHeight: 1.6,
          marginBottom: 24, textAlign: "left",
        }}>
          ℹ️ Can't find the email? Check your spam or junk folder. The link expires in 24 hours.
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)",
            borderRadius: 8, padding: "10px 14px", marginBottom: 16,
            fontSize: 12, color: "#f87171",
          }}>{error}</div>
        )}

        {/* Resent confirmation */}
        {resent && (
          <div style={{
            background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.2)",
            borderRadius: 8, padding: "10px 14px", marginBottom: 16,
            fontSize: 12, color: "#4ade80",
          }}>
            ✓ Verification email resent successfully!
          </div>
        )}

        {/* Resend */}
        <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
          Didn't receive it?{" "}
          <button
            onClick={handleResend}
            disabled={countdown > 0 || loading}
            style={{
              background: "none", border: "none",
              cursor: countdown > 0 ? "not-allowed" : "pointer",
              color: countdown > 0 ? "#374151" : "#818cf8",
              fontSize: 12, fontWeight: 600, padding: 0,
            }}
          >
            {loading ? "Sending…" : countdown > 0 ? `Resend in ${countdown}s` : "Resend email"}
          </button>
        </p>

      </div>
    </div>
  );
}
